import { useState } from "react";
import { Handshake, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../api";
import { useLang } from "../i18n";
import { Reveal, SectionHead } from "../components/Section";
import { CONTACT_CATEGORIES, PARTNERSHIP_TYPES } from "../data/site";

const inputCls = "w-full rounded-xl border border-lakou-gold/25 bg-lakou-raise/70 px-4 py-3 text-sm text-lakou-cream placeholder:text-lakou-sand focus:outline-none focus:border-lakou-gold transition-colors";
const selectCls = inputCls + " appearance-none";

export default function Contact() {
    const { t } = useLang();
    const [tab, setTab] = useState("contact");
    const [busy, setBusy] = useState(false);
    const [c, setC] = useState({ name: "", email: "", category: CONTACT_CATEGORIES[0], message: "" });
    const [s, setS] = useState({ company: "", contact_person: "", email: "", phone: "", interest: PARTNERSHIP_TYPES[0], message: "" });

    const sendContact = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const { data } = await api.post("/contact", c);
            toast.success(data.message);
            setC({ name: "", email: "", category: CONTACT_CATEGORIES[0], message: "" });
        } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
    };

    const sendSponsor = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const { data } = await api.post("/sponsorships", s);
            toast.success(data.message);
            setS({ company: "", contact_person: "", email: "", phone: "", interest: PARTNERSHIP_TYPES[0], message: "" });
        } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
    };

    return (
        <div data-testid="contact-page" className="pt-32 pb-24">
            <div className="max-w-3xl mx-auto px-4 sm:px-6">
                <SectionHead eyebrow="Talk to us" title={t("contact_title")} center testId="contact-title" />
                <Reveal delay={0.1}>
                    <div className="mt-10 flex rounded-full border border-lakou-gold/20 bg-lakou-coal/70 p-1.5" data-testid="contact-tabs">
                        {[["contact", t("contact_general"), Mail], ["partners", t("contact_partner"), Handshake]].map(([k, label, Icon]) => (
                            <button key={k} onClick={() => setTab(k)} data-testid={`contact-tab-${k}`}
                                className={`flex-1 inline-flex items-center justify-center gap-2 rounded-full py-3 font-display font-bold text-xs uppercase tracking-[0.12em] transition-colors duration-300 ${tab === k ? "bg-lakou-orange text-white" : "text-lakou-grey hover:text-lakou-cream"}`}>
                                <Icon size={14} /> {label}
                            </button>
                        ))}
                    </div>
                </Reveal>

                {tab === "contact" ? (
                    <Reveal delay={0.15}>
                        <form onSubmit={sendContact} className="mt-8 rounded-3xl border border-lakou-gold/20 bg-lakou-coal/70 p-7 sm:p-8 space-y-4" data-testid="contact-form">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <input required placeholder="Your name" value={c.name} onChange={(e) => setC({ ...c, name: e.target.value })} data-testid="contact-name" className={inputCls} />
                                <input required type="email" placeholder="Email" value={c.email} onChange={(e) => setC({ ...c, email: e.target.value })} data-testid="contact-email" className={inputCls} />
                            </div>
                            <select value={c.category} onChange={(e) => setC({ ...c, category: e.target.value })} data-testid="contact-category" className={selectCls}>
                                {CONTACT_CATEGORIES.map((x) => <option key={x} value={x}>{x}</option>)}
                            </select>
                            <textarea required rows={5} placeholder={t("form_message")} value={c.message} onChange={(e) => setC({ ...c, message: e.target.value })} data-testid="contact-message" className={inputCls} />
                            <button type="submit" disabled={busy} data-testid="contact-submit"
                                className="w-full rounded-full bg-lakou-orange hover:bg-lakou-gold text-white hover:text-lakou-ink font-display font-bold text-xs uppercase tracking-[0.15em] py-4 transition-colors duration-300 disabled:opacity-50">
                                {busy ? "…" : t("contact_send")}
                            </button>
                        </form>
                    </Reveal>
                ) : (
                    <Reveal delay={0.15}>
                        <form onSubmit={sendSponsor} className="mt-8 rounded-3xl border border-lakou-gold/20 bg-lakou-coal/70 p-7 sm:p-8 space-y-4" data-testid="partner-form">
                            <p className="text-sm text-lakou-grey leading-relaxed">
                                LAKOU partners with brands that believe in Haitian culture and world-class experiences. Tell us who you are.
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <input required placeholder="Company" value={s.company} onChange={(e) => setS({ ...s, company: e.target.value })} data-testid="partner-company" className={inputCls} />
                                <input required placeholder="Contact person" value={s.contact_person} onChange={(e) => setS({ ...s, contact_person: e.target.value })} data-testid="partner-contact" className={inputCls} />
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <input required type="email" placeholder="Email" value={s.email} onChange={(e) => setS({ ...s, email: e.target.value })} data-testid="partner-email" className={inputCls} />
                                <input placeholder="Phone" value={s.phone} onChange={(e) => setS({ ...s, phone: e.target.value })} data-testid="partner-phone" className={inputCls} />
                            </div>
                            <select value={s.interest} onChange={(e) => setS({ ...s, interest: e.target.value })} data-testid="partner-interest" className={selectCls}>
                                {PARTNERSHIP_TYPES.map((x) => <option key={x} value={x}>{x}</option>)}
                            </select>
                            <textarea required rows={5} placeholder="Tell us about the partnership you have in mind" value={s.message} onChange={(e) => setS({ ...s, message: e.target.value })} data-testid="partner-message" className={inputCls} />
                            <button type="submit" disabled={busy} data-testid="partner-submit"
                                className="w-full rounded-full bg-lakou-orange hover:bg-lakou-gold text-white hover:text-lakou-ink font-display font-bold text-xs uppercase tracking-[0.15em] py-4 transition-colors duration-300 disabled:opacity-50">
                                {busy ? "Sending…" : "Submit partnership inquiry"}
                            </button>
                        </form>
                    </Reveal>
                )}
            </div>
        </div>
    );
}
